import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import './AddressService.scss'
import {
	CircleSpinner,
	customerDataApiService,
	getErrorWithFullMessage,
} from 'exports'
import { ICustomer, IAddressService } from 'interfaces/i-api-loan'

export const AddressService = ({
	onSelectAddress,
	user,
}: {
	onSelectAddress: (address: IAddressService) => void
	user: ICustomer
}) => {
	const [inputAddress, setInputAddress] = useState('')
	const [addressResults, setAddressResults] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = React.useState<Error | null>(null)
	const { t } = useTranslation()
	const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setInputAddress(value)

		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current)
		}

		debounceTimeout.current = setTimeout(async () => {
			if (value.length >= 3) {
				setLoading(true)

				try {
					const response =
						await customerDataApiService.lookupAddressInAddressService(
							value,
						)

					if (!response) {
						throw new Error(t('FailedToFetchAddressData'))
					}

					if (response.length === 0) {
						setAddressResults([])
						setError(new Error(t('NoAddressesFound')))
						return
					}
					setError(null)
					setAddressResults(response)
				} catch (err) {
					setError(
						getErrorWithFullMessage(
							err instanceof Error
								? err
								: new Error(t('UnknownError')),
						),
					)
				} finally {
					setLoading(false)
				}
			} else {
				setAddressResults([])
				setError(null)
			}
		}, 300)
	}

	const handleAddressSelect = (selectedAddress: IAddressService) => {
		onSelectAddress(selectedAddress)
		setInputAddress(
			selectedAddress.address + ' ' + selectedAddress.postalCode,
		)
		setAddressResults([])
	}

	return (
		<div className="address-service">
			<label htmlFor="address-input" className="label-input">
				{t('Addressline')}
			</label>
			<input
				className="input-form"
				type="text"
				value={inputAddress}
				onChange={handleInputChange}
				placeholder={
					user.address.city || user.address.street || user.address.zip
						? `${user.address.street || ''} ${
								user.address.city || ''
							} ${user.address.zip || ''}`.trim()
						: t('EnterAddress')
				}
			/>
			{loading && <CircleSpinner />}

			{error && <p className="error">{error.message}</p>}

			{addressResults.length > 0 && (
				<div className="address-output-wrapper">
					<ul>
						{addressResults.map(
							(address: IAddressService, index) => (
								<li
									key={index}
									onClick={() =>
										handleAddressSelect(address)
									}>
									{`${address.address} ${address.postalCode}`}
								</li>
							),
						)}
					</ul>
				</div>
			)}
		</div>
	)
}
export default AddressService
